import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { userApi } from "@/api/user.api";
import { api } from "@/api/client";
import { decode, JwtPayload } from "jsonwebtoken-esm";
import { queryClient } from "./queryClient";
import { authApi } from "@/api/authentification.api";
import { storeToRefs } from "pinia";

export async function fetchTokenQuery() {
  return await queryClient.fetchQuery({
    queryKey: ['app-token'],
    staleTime: 5000,
    gcTime: Infinity,
    queryFn: () => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        const decodedToken = decode(token);
        if (!decodedToken) return null;
        if ((decodedToken as JwtPayload).exp && (decodedToken as JwtPayload).exp * 1000 < Date.now()) return null;
        return { token: token, decoded: decodedToken };
      }
      return null;
    },
  });
}

export async function fetchUserQuery() {
  return await queryClient.fetchQuery({
    queryKey: ['user'],
    queryFn: () => userApi.getUser(),
  });
}

export function prefetchUserRoles() {
  const appStore = storeToRefs(useAppStore());
  return queryClient.prefetchQuery({
    queryKey: ['roles', { organisationId: appStore.currentOrganisationId }],
    queryFn: async () => {
      const roles = await userApi.role(toValue(appStore.currentOrganisationId));
      roles.forEach((r) => {
        queryClient.setQueryData(['roles', { organisationId: appStore.currentOrganisationId, roleId: r.id }], r);
      });
      return roles;
    }
  });
}

export function useUser() {
  return useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      await fetchTokenQuery();
      return await userApi.getUser();
    }
  });
}

export function useRoles() {
  const appStore = storeToRefs(useAppStore());
  return useQuery({
    queryKey: ['roles', { organisationId: appStore.currentOrganisationId }],
    queryFn: async () => {
      const roles = await userApi.role(toValue(appStore.currentOrganisationId));
      roles.forEach((r) => {
        queryClient.setQueryData(['roles', { organisationId: appStore.currentOrganisationId, roleId: r.id }], r);
      });
      return roles;
    }
  });
}

export function useSubmitCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { email: string, code: string, accountId?: string }) => authApi.submitCode(data.email, data.code, data.accountId),
    onSuccess: (token) => {
      localStorage.setItem('auth_token', token);
      queryClient.invalidateQueries({ queryKey: ['app-token'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['organisations'] });
      //api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  });
}

export function useRegister() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { email: string, password: string, firstName: string, lastName: string, phoneNumber: string }) => userApi.register(data.email, data.password, data.firstName, data.lastName, data.phoneNumber),
    onSuccess: (data) => {
      queryClient.setQueryData(['user'], data.user);
      queryClient.setQueryData(['app-token'], { token: data.token, decoded: decode(data.token) });
      queryClient.invalidateQueries({ queryKey: ['organisations'] });
      localStorage.setItem('auth_token', data.token);
      api.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
      axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
    }
  });
}

export function logout() {
  console.log("call logout");
  localStorage.removeItem('auth_token');
  localStorage.removeItem("redirectUrl");
  // delete api.defaults.headers.common["Authorization"];
  delete axiosInstance.defaults.headers.common["Authorization"];
  queryClient.clear();
}

export function useUpdateUserPassword() {
  return useMutation({
    mutationFn: (data: { oldPassword: string, newPassword: string }) => userApi.updatePassword(data.oldPassword, data.newPassword),
  });
}

export function useUpdateProfilePicture() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (newImageId: string) => userApi.updateProfilePicture(newImageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
    }
  });
}